// SAM-973 — the Estate tab groups a user's units by request status. The status table is data
// (GET /user_estate_status); only 1 = pending and 2 = approved are known ids, so the groups are
// found by name first and by id second. Anything that is neither pending nor approved
// (declined, rejected, revoked, suspended…) lands in "Declined".

export type EstateStatusKind = 'pending' | 'active' | 'declined';

export interface EstateStatusLike {
  id?: number;
  name?: string | null;
}

/** sam_core.user_estate_status ids (prod catalogue, 19 Oct). The groups and the actions go by id, not by name. */
export const ESTATE_STATUS = {
  PROCESSING: 1,
  ACTIVE: 2,
  EXPIRED: 3,
  SUSPENDED: 4,
  DECLINED: 5,
  REMOVED: 6,
  CONTACT_SUPPORT: 7,
} as const;

/** Pending = Processing and Contact Support; Active = Active and Expired; the rest is Declined. */
export function estateStatusKind(status: EstateStatusLike | null | undefined, statusId?: number | null): EstateStatusKind {
  const id = Number(status?.id ?? statusId);
  if (id === ESTATE_STATUS.PROCESSING || id === ESTATE_STATUS.CONTACT_SUPPORT) return 'pending';
  if (id === ESTATE_STATUS.ACTIVE || id === ESTATE_STATUS.EXPIRED) return 'active';
  return 'declined';
}

/** Statuses that share a group with another one and need their own label in the row. */
export const needsStatusLabel = (statusId: number): boolean =>
  statusId === ESTATE_STATUS.EXPIRED ||
  statusId === ESTATE_STATUS.SUSPENDED ||
  statusId === ESTATE_STATUS.REMOVED ||
  statusId === ESTATE_STATUS.CONTACT_SUPPORT;

/** Whole days from `today` to a 'YYYY-MM-DD' (or ISO) expiration; negative once passed; null without a date. */
export function daysUntil(expiration: string | null | undefined, today: Date = new Date()): number | null {
  const day = estateExpirationDay(expiration);
  if (!day) return null;
  const [y, m, d] = day.split('-').map(Number);
  const start = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((Date.UTC(y, m - 1, d) - start) / 86_400_000);
}

/** The calendar day of an expiration value, 'YYYY-MM-DD'; null when there is none. */
export function estateExpirationDay(expiration: string | null | undefined): string | null {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(expiration ?? ''));
  return m ? m[1] : null;
}
